import React, { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useEditMode } from '../context/EditModeContext';

/**
 * AddBlockButton
 * --------------------------------------------------------------------
 * Inline "+" button to insert a new block. Renders nothing outside of
 * edit mode. Clicking opens a small popup menu of available block types.
 *
 * Props:
 *   onAdd          (blockType) => void
 *   availableTypes string[]
 *   className      ?string
 */
function AddBlockButton({ onAdd, availableTypes = [], className = '' }) {
  const { editMode } = useEditMode();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!editMode) return null;

  return (
    <div ref={ref} className={`relative flex items-center justify-center my-2 ${className}`}>
      <div className="absolute inset-x-0 top-1/2 h-px bg-indigo-200/60" />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Add block"
        className="relative z-10 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-600 bg-white border border-dashed border-indigo-300 rounded-full hover:bg-indigo-50"
      >
        <Plus size={14} />
        Add block
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[180px]">
          {availableTypes.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">No block types available</div>
          )}
          {availableTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                setOpen(false);
                onAdd && onAdd(type);
              }}
              className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
            >
              {type}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AddBlockButton;
